'use client';

import type { CSSProperties } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useWikiSource, useBlockPart, type WikiTreeNode } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Wiki tree navigation block. Two layouts:
 * <ul>
 *   <li>{@code columns} — grid of top-level sections, each with its
 *       immediate children listed underneath.</li>
 *   <li>{@code list} — vertical accordion of sections with body
 *       excerpts.</li>
 * </ul>
 */
export interface WikiTreeBlockProps {
  title?: string;
  /** Pin to a subtree — when set, only the children of this slug
   *  render. */
  rootSlug?: string;
  /** How deep to descend into each section's children. Default 1. */
  maxDepth?: number;
  layout?: 'columns' | 'list';
  /** href template per page. Default {@code /wiki/{slug}}. */
  pageHrefTemplate?: string;
  /** Server-fetched tree for SSR; when provided, renders directly
   *  (no client WikiSource fetch). */
  tree?: WikiTreeNode[];
}

function fillTemplate(tpl: string, slug: string): string {
  return tpl.replace('{slug}', slug);
}

function findNode(tree: WikiTreeNode[], slug: string): WikiTreeNode | null {
  for (const n of tree) {
    if (n.slug === slug) return n;
    const inChild = findNode(n.children, slug);
    if (inChild) return inChild;
  }
  return null;
}

export function WikiTreeBlock({
  title: authoredTitle,
  rootSlug,
  maxDepth = 1,
  layout = 'columns',
  pageHrefTemplate = '/wiki/{slug}',
  tree: treeProp,
}: WikiTreeBlockProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const title = authoredTitle ?? t('shared.label.documentation');
  const wiki = useWikiSource();

  const state = useAsyncResource<WikiTreeNode[]>(async () => {
    if (treeProp !== undefined) return null; // ignored when prop provided
    if (!wiki) return null;
    const tree = await wiki.getTree();
    return tree.length > 0 ? tree : null;
  }, [wiki, treeProp]);

  // Prop path (SSR) resolves synchronously; client path uses the resource.
  const data: WikiTreeNode[] | null | undefined =
    treeProp !== undefined
      ? treeProp.length > 0
        ? treeProp
        : null
      : state.kind === 'loaded'
        ? state.data
        : undefined;

  if (treeProp === undefined && !wiki) {
    return (
      <BlockRoot as="section" style={sectionStyle}>
        <div style={containerStyle}>
          <AdapterPlaceholder blockType="WikiTreeBlock" adapterName="WikiSource" />
        </div>
      </BlockRoot>
    );
  }

  if (data === undefined || data === null) return null;

  const sections = rootSlug ? (findNode(data, rootSlug)?.children ?? []) : data;
  if (sections.length === 0) return null;

  return (
    <BlockRoot as="section" style={sectionStyle}>
      <div style={containerStyle}>
        <Title text={title} />
        {layout === 'columns' ? (
          <div style={gridStyle} {...part('list')}>
            {sections.map((section) => (
              <SectionColumn
                key={section.slug}
                section={section}
                template={pageHrefTemplate}
                maxDepth={maxDepth}
              />
            ))}
          </div>
        ) : (
          <ul style={listContainerStyle} {...part('list')}>
            {sections.map((section) => (
              <SectionListItem
                key={section.slug}
                section={section}
                template={pageHrefTemplate}
                maxDepth={maxDepth}
              />
            ))}
          </ul>
        )}
      </div>
    </BlockRoot>
  );
}

const sectionStyle: CSSProperties = {
  padding: 'var(--space-12, 48px) 0',
  background: 'var(--color-surface-page, #fafafa)',
};

const containerStyle: CSSProperties = {
  maxWidth: 1200,
  margin: '0 auto',
  padding: '0 var(--space-6, 24px)',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: 'var(--space-6, 24px)',
};

const listContainerStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

function Title({ text }: { text: string }) {
  return (
    <h2
      style={{
        margin: '0 0 var(--space-6, 24px)',
        fontSize: 'clamp(22px, 3vw, 28px)',
        fontWeight: 600,
        letterSpacing: 'var(--font-tracking-snug, -0.01em)',
        fontFamily: 'var(--font-family-display, inherit)',
        color: 'var(--color-text-primary, #18181b)',
      }}
    >
      {text}
    </h2>
  );
}

function SectionColumn({
  section,
  template,
  maxDepth,
}: {
  section: WikiTreeNode;
  template: string;
  maxDepth: number;
}) {
  return (
    <div>
      <Link
        href={fillTemplate(template, section.slug)}
        style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 'var(--font-tracking-wider, 0.06em)',
          color: 'var(--color-text-primary, #18181b)',
          textDecoration: 'none',
          marginBottom: 'var(--space-3, 12px)',
        }}
      >
        {section.title}
      </Link>
      {maxDepth > 0 && section.children.length > 0 && (
        <ChildList nodes={section.children} template={template} depth={1} maxDepth={maxDepth} />
      )}
    </div>
  );
}

function SectionListItem({
  section,
  template,
  maxDepth,
}: {
  section: WikiTreeNode;
  template: string;
  maxDepth: number;
}) {
  return (
    <li
      style={{
        padding: 'var(--space-4, 16px) 0',
        borderBottom: '1px solid var(--color-neutral-100, #f4f4f5)',
      }}
    >
      <Link
        href={fillTemplate(template, section.slug)}
        style={{
          fontSize: 'var(--font-size-md, 16px)',
          fontWeight: 600,
          color: 'var(--color-text-primary, #18181b)',
          textDecoration: 'none',
        }}
      >
        {section.title}
      </Link>
      <div
        style={{
          marginTop: 4,
          fontSize: '13px',
          color: 'var(--color-text-secondary, #71717a)',
        }}
      >
        {section.excerpt}
      </div>
      {maxDepth > 0 && section.children.length > 0 && (
        <ChildList nodes={section.children} template={template} depth={1} maxDepth={maxDepth} />
      )}
    </li>
  );
}

function ChildList({
  nodes,
  template,
  depth,
  maxDepth,
}: {
  nodes: WikiTreeNode[];
  template: string;
  depth: number;
  maxDepth: number;
}) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: depth === 1 ? '0' : '4px 0 0' }}>
      {nodes.map((node) => (
        <li
          key={node.slug}
          style={{ marginTop: depth === 1 ? 6 : 4, paddingLeft: (depth - 1) * 12 }}
        >
          <Link
            href={fillTemplate(template, node.slug)}
            style={{
              fontSize: depth === 1 ? 14 : 13,
              color: 'var(--color-text-secondary, #52525b)',
              textDecoration: 'none',
            }}
          >
            {node.title}
          </Link>
          {depth < maxDepth && node.children.length > 0 && (
            <ChildList
              nodes={node.children}
              template={template}
              depth={depth + 1}
              maxDepth={maxDepth}
            />
          )}
        </li>
      ))}
    </ul>
  );
}
